/* ==========================================================================
   Fine Threads — Sticky mobile Add to Cart bar behaviour

   - Reveals the bar once the main Add to Cart button has scrolled out of view
     upward (IntersectionObserver).
   - Keeps the bar's price + button state in sync with the selected variant by
     observing Dawn's own #price-{section} block and #ProductSubmitButton-{section}
     (both are re-rendered by product-info.js when the variant/size changes).
   - The bar's button delegates its click to the real submit button, so the add
     runs through Dawn's product-form.js (real /cart/add.js + cart drawer).
   ========================================================================== */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.mainButton = null;
        this.priceSource = null;
        this.stickyButton = this.querySelector('[data-ft-sticky-add]');
        this.stickyButtonText = this.querySelector('.ft-sticky-atc__button-text');
        this.stickyPrice = this.querySelector('[data-ft-sticky-price]');
      }

      connectedCallback() {
        this.mainButton = document.getElementById(this.dataset.target);
        this.priceSource = document.getElementById(this.dataset.priceTarget);
        if (!this.mainButton) return;

        // Delegate the add to the real product form.
        this.stickyButton?.addEventListener('click', () => {
          if (this.stickyButton.disabled) return;
          this.mainButton.click();
        });

        this.setupVisibility();
        this.setupSync();
        this.syncButton();
        this.syncPrice();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
      }

      setupVisibility() {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            // Show only once the main button has scrolled ABOVE the viewport.
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('ft-sticky-atc--visible', scrolledPast);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.mainButton);
      }

      setupSync() {
        // Mirror the main button's disabled state + label text.
        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        // Mirror the selected-variant price.
        if (this.priceSource) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.priceSource, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      syncButton() {
        if (!this.mainButton || !this.stickyButton) return;
        const disabled =
          this.mainButton.hasAttribute('disabled') ||
          this.mainButton.getAttribute('aria-disabled') === 'true';
        this.stickyButton.disabled = disabled;

        const label = this.mainButton.querySelector('span');
        if (label && this.stickyButtonText) {
          this.stickyButtonText.textContent = label.textContent.trim();
        }
      }

      syncPrice() {
        if (!this.priceSource || !this.stickyPrice) return;
        // Prefer the visible sale price, else the regular price.
        const sale = this.priceSource.querySelector('.price-item--sale:not(.hidden)');
        const regular = this.priceSource.querySelector('.price-item--regular');
        const source = sale || regular;
        if (source) {
          this.stickyPrice.textContent = source.textContent.trim();
        }
      }
    }
  );
}
